import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiGift, 
  FiUsers, 
  FiDollarSign, 
  FiTrendingUp,
  FiCopy,
  FiShare2,
  FiGrid,
  FiCalendar,
  FiMail
} from 'react-icons/fi';
import { useNotification } from '../../contexts/NotificationContext';
import Button from '../../components/common/Button';
// import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { referralAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { QRCodeSVG } from 'qrcode.react';
import { useAppConfig } from '../../contexts/AppConfigContext';

const ReferralDashboard = () => {
  // const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { appName } = useAppConfig();
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    pendingRewards: 0,
    completedRewards: 0,
    subscriptionCommissions: {
      totalAmount: 0,
      totalCommissions: 0
    }
  });
  const [referralCode, setReferralCode] = useState('');
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [generating, setGenerating] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await referralAPI.getMyStats();
      const payload = response?.data;

      if (payload?.success) {
        const data = payload.data || {};

        // Derive stats compatible with existing UI
        const rewards = Array.isArray(data.rewards) ? data.rewards : [];
        const pendingRewards = rewards.filter(r => r.status === 'pending').length;
        const completedRewards = rewards.filter(r => r.status === 'sent' || r.status === 'completed').length;

        setStats({
          totalReferrals: data.totalReferrals || 0,
          totalEarnings: data.totalEarnings || 0,
          pendingRewards,
          completedRewards,
          subscriptionCommissions: {
            totalAmount: data.subscriptionCommissions?.totalAmount || 0,
            totalCommissions: data.subscriptionCommissions?.totalCommissions || 0
          }
        });

        setReferralCode(data.referralCode || '');

        const mappedReferrals = (Array.isArray(data.referrals) ? data.referrals : []).slice(0, 10).map((ref) => {
          const user = ref.user || {};
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
          return {
            id: user._id || ref._id || ref.id,
            name: fullName || user.email || 'Referred user',
            email: user.email || '',
            joinedAt: ref.registeredAt || user.createdAt || ref.createdAt || new Date().toISOString(),
            status: ref.rewardSent ? 'reward_sent' : 'active'
          };
        });
        setRecentReferrals(mappedReferrals);
      } else {
        showError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const generateNewCode = async () => {
    try {
      setGenerating(true);
      const response = await referralAPI.generateUserCode();
      const payload = response?.data;

      if (payload?.success && payload?.data?.referralCode) {
        setReferralCode(payload.data.referralCode);
        showSuccess('New referral code generated successfully!');
      } else {
        showError('Failed to generate new referral code');
      }
    } catch (error) {
      console.error('Error generating new code:', error);
      showError('Failed to generate new referral code');
    } finally {
      setGenerating(false);
    }
  };

  const generateReferralCode = generateNewCode;

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    showSuccess('Referral code copied to clipboard!');
  };

  const copyReferralLink = () => {
    const referralUrl = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralUrl);
    showSuccess('Referral link copied to clipboard!');
  };

  const shareReferralCode = () => {
    const referralUrl = `${window.location.origin}/register?ref=${referralCode}`;
    const shareText = `Join ${appName} using my referral code ${referralCode} and get N200 airtime bonus! ${referralUrl}`;
    
    if (navigator.share) {
      navigator.share({
        title: `Join ${appName}`,
        text: shareText,
        url: referralUrl
      });
    } else {
      // Fallback to copying
      navigator.clipboard.writeText(shareText);
      showSuccess('Referral message copied to clipboard!');
    }
  };

  const downloadQRCode = () => {
    const canvas = document.querySelector('#qr-code-canvas canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `referral-qr-${referralCode}.png`;
      link.href = canvas.toDataURL();
      link.click();
      showSuccess('QR code downloaded successfully!');
    } else {
      showError('Failed to download QR code');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>Loading referral dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Referral Dashboard</h1>
        <p>Earn rewards by referring friends to {appName}</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FiUsers size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.totalReferrals || 0}</h3>
            <p>Total Referrals</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiDollarSign size={24} />
          </div>
          <div className="stat-content">
            <h3>₦{stats?.totalEarnings || 0}</h3>
            <p>Total Earnings</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiGift size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats?.pendingRewards || 0}</h3>
            <p>Pending Rewards</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiTrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>₦{stats?.subscriptionCommissions?.totalAmount || 0}</h3>
            <p>Subscription Commissions</p>
          </div>
        </div>
      </div>

      {/* Referral Code Section */}
      <div className="referral-section">
        <div className="section-header">
          <div className="section-title-with-info">
            <h2>Your Referral Code</h2>
            <span
              className="info-icon"
              title="Share your code or link. When friends register and subscribe, you earn commissions and bonuses."
              aria-label="Referral benefits info"
            >
              i
            </span>
          </div>
          <p>Share this code with friends to earn rewards</p>
        </div>
        
        {referralCode ? (
          <div className="referral-code-container">
            <div className="referral-code-display">
              <div className="code-text">{referralCode}</div>
              <div className="code-actions">
                <Button
                  variant="outline"
                  size="small"
                  onClick={copyReferralCode}
                  className="action-btn"
                >
                  <FiCopy size={16} />
                  Copy Code
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={copyReferralLink}
                  className="action-btn"
                >
                  <FiShare2 size={16} />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setShowQR(!showQR)}
                  className="action-btn"
                >
                  <FiGrid size={16} />
                  {showQR ? 'Hide QR' : 'Show QR'}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={shareReferralCode}
                  className="action-btn"
                >
                  <FiShare2 size={16} />
                  Share
                </Button>
              </div>
            </div>
            
            {showQR && (
              <div className="qr-code-container" id="qr-code-canvas">
                <div className="qr-skeleton" aria-hidden="true" />
                <QRCodeSVG
                  value={`${window.location.origin}/register?ref=${referralCode}`}
                  size={200}
                  level="M"
                  includeMargin={true}
                />
                <p className="qr-description">Scan this QR code to register with your referral code</p>
                <Button
                  variant="outline"
                  size="small"
                  onClick={downloadQRCode}
                  className="download-qr-btn"
                >
                  Download QR Code
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-referral-code">
            <p>You don't have a referral code yet. Generate one to start earning!</p>
            <Button
              variant="primary"
              onClick={generateReferralCode}
              disabled={generating}
            >
              {generating ? <LoadingSpinner size="small" /> : 'Generate Referral Code'}
            </Button>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="how-it-works-section">
        <h2>How Referrals Work</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Share Your Code</h3>
            <p>Share your unique referral code with friends and family</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Friend Registers</h3>
            <p>When they register using your code, they get ₦200 airtime bonus</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>You Earn Rewards</h3>
            <p>You receive ₦100 airtime bonus for each successful referral</p>
          </div>
        </div>
      </div>

      {/* Recent Referrals */}
      <div className="recent-referrals-section">
        <h2>Recent Referrals</h2>
        <div className="referrals-list">
          {recentReferrals.length > 0 ? (
            recentReferrals.map((referral, index) => (
              <div key={referral.id || index} className="referral-item">
                <div className="referral-info">
                  <div className="referral-name">{referral.name}</div>
                  <div className="referral-email">
                    <FiMail size={14} />
                    {referral.email}
                  </div>
                  <div className="referral-date">
                    <FiCalendar size={14} />
                    Joined {new Date(referral.joinedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="referral-status">
                  <Badge 
                    variant={referral.status === 'active' ? 'success' : 'warning'}
                    size="small"
                  >
                    {referral.status}
                  </Badge>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <FiUsers size={48} />
              <p>No referrals yet. Start sharing your referral code!</p>
            </div>
          )}
        </div>
      </div>
      
      <style jsx>{`
        .section-title-with-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .info-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          font-size: 12px;
          line-height: 1;
          border-radius: 50%;
          border: 1px solid #cbd5e1;
          color: #475569;
          cursor: default;
        }
        .qr-code-section {
          margin-top: 1rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        
        .qr-code-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .qr-skeleton {
          width: 200px;
          height: 200px;
          border-radius: 8px;
          background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 37%, #e2e8f0 63%);
          background-size: 400% 100%;
          animation: shimmer 1.2s ease-in-out infinite;
          position: absolute;
          opacity: 0.6;
          pointer-events: none;
        }
        @keyframes shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        
        .qr-description {
          text-align: center;
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }
        
        .download-qr-btn {
          margin-top: 0.5rem;
        }
        
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          color: #64748b;
          text-align: center;
        }
        
        .empty-state svg {
          margin-bottom: 1rem;
          opacity: 0.5;
        }
        
        .referral-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          margin-bottom: 0.5rem;
        }
        
        .referral-info {
          flex: 1;
        }
        
        .referral-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        
        .referral-email,
        .referral-date {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.25rem;
        }
        
        .referral-status {
          margin-left: 1rem;
        }
      `}</style>
    </div>
  );
};

export default ReferralDashboard;